import React from "react";
import {
  isHtmlString,
  sanitizeCardDescription,
} from "@/lib/visual-cms/sanitize-rich-text";

export interface CardDescriptionHtmlProps {
  /**
   * Raw card description from Visual CMS storage. Can be either:
   *   - legacy plain text (saved before the rich editor existed), or
   *   - HTML emitted by the card-description rich editor.
   */
  text: string | null | undefined;
  /** Wrapper element. Defaults to <span> so it can sit inside <p> / <li>. */
  as?: keyof JSX.IntrinsicElements;
  className?: string;
  /**
   * When the value is plain text containing newlines, render line breaks as
   * `<br>` (whitespace-pre-line). Has no effect on HTML content (the editor
   * emits its own paragraph/break markup).
   */
  preserveLineBreaks?: boolean;
}

/**
 * Renders a Visual CMS card description with sanitised inline HTML for
 * bold / italic / link formatting. Falls back to plain-text rendering for
 * legacy rows that don't contain markup.
 *
 * Sanitisation is delegated to `sanitizeCardDescription` (DOMPurify with a
 * tight allow-list matching the card-description editor's schema).
 */
export const CardDescriptionHtml: React.FC<CardDescriptionHtmlProps> = ({
  text,
  as: Tag = "span",
  className,
  preserveLineBreaks = false,
}) => {
  const value = (text ?? "").trim();
  if (!value) return null;

  if (!isHtmlString(value)) {
    // Plain-text path — preserve newlines when asked so legacy multi-line
    // strings keep their visual line breaks without going through the
    // sanitiser.
    return (
      <Tag
        className={`${className ?? ""} ${preserveLineBreaks ? "whitespace-pre-line" : ""}`.trim()}
      >
        {value}
      </Tag>
    );
  }

  const safeHtml = sanitizeCardDescription(value);
  return (
    <Tag
      className={className}
      // Sanitised above. Markup is restricted to {p, br, strong, b, em, i, a}
      // and links are forced to rel="noopener noreferrer" target="_blank".
      dangerouslySetInnerHTML={{ __html: safeHtml }}
    />
  );
};
